import { useEffect, useState } from 'react';
import { cn } from '@/lib/cn';

/**
 * A company's visual mark, best-effort: explicit logoUrl → website favicon
 * (Google s2 service) → display-face monogram tile. The monogram is also the
 * error fallback, so a dead URL never leaves a broken image.
 */
export function CompanyLogo({
  name,
  websiteUrl,
  logoUrl,
  size = 36,
  className,
}: {
  name: string;
  websiteUrl?: string | null;
  logoUrl?: string | null;
  /** Rendered box in px (20 sidebar, 36 cards, 48 page header). */
  size?: number;
  className?: string;
}) {
  const src = logoUrl ?? faviconFor(websiteUrl);
  const [failed, setFailed] = useState(false);

  // A new source deserves a fresh attempt (e.g. after editing the URL in settings).
  useEffect(() => setFailed(false), [src]);

  // Square marks, hairline border, breathing room — favicons are square
  // artwork; rounding them clips corners and reads cheap.
  if (!src || failed) {
    return (
      <span
        aria-hidden
        style={{ width: size, height: size, fontSize: size * 0.44 }}
        className={cn(
          'inline-flex shrink-0 select-none items-center justify-center rounded-[3px] border border-heading/15 bg-surface font-display font-semibold text-heading',
          className,
        )}
      >
        {(name.trim()[0] ?? '?').toUpperCase()}
      </span>
    );
  }

  // Real artwork gets no container at all — favicons carry their own corner
  // treatment, and any box we draw around them fights it.
  return (
    <img
      src={src}
      alt=""
      aria-hidden
      width={size}
      height={size}
      style={{ width: size, height: size }}
      onError={() => setFailed(true)}
      className={cn('shrink-0 select-none object-contain', className)}
    />
  );
}

function faviconFor(websiteUrl?: string | null): string | null {
  if (!websiteUrl) return null;
  try {
    const { hostname } = new URL(websiteUrl);
    return `https://www.google.com/s2/favicons?domain=${hostname}&sz=64`;
  } catch {
    return null;
  }
}
